import {
  SupplierRelationshipFulfillmentType,
  SupplierGetResponseType,
  SupplierStorefrontV2GetResponseType,
} from "@sellar-io/api-v2/types";
import { CollectionOptionsCard } from "../../../components/fulfillmentTypeCards/CollectionOptionsCard";
import { CourierDeliveryOptionsCard } from "../../../components/fulfillmentTypeCards/CourierDeliveryOptionsCard";
import { DirectDeliveryOptionsCard } from "../../../components/fulfillmentTypeCards/DirectDeliveryOptionsCard";
import { HaulierDeliveryOptionsCard } from "../../../components/fulfillmentTypeCards/HaulierDeliveryOptionsCard";
import {
  RadioGroup,
  RadioGroupOption,
  RadioGroupContainer,
} from "../../../components/radioSelector/RadioGroup";
import { ContextOrderBasketType } from "../../../hooks";
import { LegacyOrderCheckoutType } from "../checkoutV2/lib/types";
import { DistributionDeliveryOptionsCard } from "../../../components/fulfillmentTypeCards/DistributionDeliveryOptionsCard";
import { IneligibleItemsDialog } from "./IneligibleItemsDialog";
import { isOrderItemEligibleForDeliveryMethod } from "@sellar-io/common";
import { useState } from "react";
import { CheckoutSelectedFulfillmentType } from "..";
import { twMerge } from "tailwind-merge";
import { useFulfillmentTypeAutoSelect } from "../../../hooks/useFulfillmentTypeAutoSelect";
import {
  getDeliveryMethodByType,
  isValidContainerUnit,
  getDeliveryMethodInitialValue,
} from "./DeliveryMethodRadioSelector.shared";

type CheckoutOrderType = {
  OrderItems: {
    Product: {
      id: number;
      availableFrom: Date | null;
      containerType?: string | null;
      unit?: string | null;
      volume?: number | null;
    };
  }[];
};

export type DeliveryMethodType = NonNullable<
  SupplierStorefrontV2GetResponseType["deliveryMethods"]
>[number];

type CompositeOrderType =
  | ContextOrderBasketType
  | LegacyOrderCheckoutType
  | CheckoutOrderType
  | null
  | undefined;

type DeliveryMethodConnection =
  | { fulfillmentType: SupplierRelationshipFulfillmentType | null }
  | undefined
  | null;

export const DeliveryMethodRadioSelector = ({
  value,
  connection,
  order,
  supplier,
  supplierStorefront,
  buyerPostcode,
  onUpdate,
  onConfirmIneligibleUpdate,
}: {
  value: CheckoutSelectedFulfillmentType;
  connection: DeliveryMethodConnection;
  order: CompositeOrderType;
  supplier: SupplierGetResponseType;
  supplierStorefront: SupplierStorefrontV2GetResponseType;
  buyerPostcode: string;
  onUpdate: (value: CheckoutSelectedFulfillmentType) => void;
  onConfirmIneligibleUpdate?: (
    value: CheckoutSelectedFulfillmentType,
  ) => Promise<void> | void;
}) => {
  const [ineligibleDialogState, setIneligibleDialogState] = useState<{
    active: boolean;
    targetFulfillmentType: CheckoutSelectedFulfillmentType | null;
    targetFulfillmentTypeData: DeliveryMethodType | null;
  }>({
    active: false,
    targetFulfillmentType: null,
    targetFulfillmentTypeData: null,
  });

  const courierDeliveryMethod = getDeliveryMethodByType(
    supplierStorefront,
    "courier",
  );

  const palletDeliveryMethod = getDeliveryMethodByType(
    supplierStorefront,
    "haulier",
  );

  const directDeliveryMethod = getDeliveryMethodByType(
    supplierStorefront,
    "direct",
  );

  const sdDeliveryMethod = getDeliveryMethodByType(
    supplierStorefront,
    "sellarDistribution",
  );

  const initialValue = getDeliveryMethodInitialValue({
    connection,
    supplierStorefront,
    supplier,
  });

  const isEligibleForCollection =
    supplier.allowCollectionOnCheckout ||
    connection?.fulfillmentType === "collection";

  const handleFulfillmentTypeChange = (
    newFulfillmentType: CheckoutSelectedFulfillmentType,
  ) => {
    // Find the fulfillment type data for the selected type
    const targetFulfillmentData = getDeliveryMethodByType(
      supplierStorefront,
      newFulfillmentType,
    );

    if (!targetFulfillmentData) {
      // If no specific data found, just update normally
      onUpdate(newFulfillmentType);
      return;
    }

    // Check if there are ineligible items for this fulfillment type
    const ineligibleItems = order?.OrderItems?.filter((item) => {
      const product = item.Product;

      if (!isValidContainerUnit(product.unit)) return true;

      return !isOrderItemEligibleForDeliveryMethod({
        deliveryMethod: targetFulfillmentData,
        item: {
          containerType: product.containerType,
          unit: product.unit,
          volume: product.volume,
        },
      });
    });

    if (ineligibleItems && ineligibleItems.length > 0) {
      // Show dialog for ineligible items
      setIneligibleDialogState({
        active: true,
        targetFulfillmentType: newFulfillmentType,
        targetFulfillmentTypeData: targetFulfillmentData,
      });
    } else {
      // No ineligible items, proceed with normal update
      onUpdate(newFulfillmentType);
    }
  };

  const handleIneligibleDialogConfirm = () => {
    if (ineligibleDialogState.targetFulfillmentType) {
      if (onConfirmIneligibleUpdate) {
        void onConfirmIneligibleUpdate(
          ineligibleDialogState.targetFulfillmentType,
        );
      } else {
        onUpdate(ineligibleDialogState.targetFulfillmentType);
      }
    }
    setIneligibleDialogState({
      active: false,
      targetFulfillmentType: null,
      targetFulfillmentTypeData: null,
    });
  };

  useFulfillmentTypeAutoSelect({
    isEligibleForDistribution: Boolean(sdDeliveryMethod),
    isEligibleForDirect: Boolean(directDeliveryMethod),
    value,
    supplierId: supplier.id,
    onUpdate,
  });

  return (
    <>
      <RadioGroup
        value={value ?? initialValue}
        onChange={(value) => {
          handleFulfillmentTypeChange(value as CheckoutSelectedFulfillmentType);
        }}
      >
        <RadioGroupContainer>
          {!!sdDeliveryMethod && (
            <RadioGroupOption
              value={"sellarDistribution"}
              radioClassName={twMerge(
                value === "sellarDistribution" && "bg-[#27272A]",
              )}
              className="p-4"
            >
              <DistributionDeliveryOptionsCard
                deliveryArea={supplierStorefront.sdDeliveryArea}
                supplier={supplier}
                directFulfillmentType={{
                  ...sdDeliveryMethod,
                  fulfillmentType: "sellarDistribution",
                  DeliveryChargeRules: [],
                }}
                orderItems={order?.OrderItems}
                buyerPostcode={buyerPostcode}
              />
            </RadioGroupOption>
          )}
          {!!directDeliveryMethod && (
            <RadioGroupOption value={"direct"} className={"p-4"}>
              <DirectDeliveryOptionsCard
                deliveryArea={supplierStorefront.directDeliveryArea}
                supplier={supplier}
                directFulfillmentType={{
                  ...directDeliveryMethod,
                  fulfillmentType: "direct",
                  DeliveryChargeRules: [],
                }}
                orderItems={order?.OrderItems}
              />
            </RadioGroupOption>
          )}
          {!!courierDeliveryMethod && (
            <RadioGroupOption value={"courier"} className={"p-4"}>
              <CourierDeliveryOptionsCard
                supplier={supplier}
                courierFulfillmentType={{
                  ...courierDeliveryMethod,
                  fulfillmentType: "courier",
                  DeliveryChargeRules: [],
                }}
                orderItems={order?.OrderItems}
              />
            </RadioGroupOption>
          )}
          {!!palletDeliveryMethod && (
            <RadioGroupOption value={"haulier"} className={"p-4"}>
              <HaulierDeliveryOptionsCard
                supplierId={supplier.id}
                haulierFulfillmentType={{
                  ...palletDeliveryMethod,
                  fulfillmentType: "haulier",
                  DeliveryChargeRules: [],
                }}
              />
            </RadioGroupOption>
          )}
          {isEligibleForCollection && (
            <RadioGroupOption value={"collection"} className={"p-4"}>
              <CollectionOptionsCard
                supplier={supplier}
                orderItems={order?.OrderItems}
              />
            </RadioGroupOption>
          )}
        </RadioGroupContainer>
      </RadioGroup>

      <IneligibleItemsDialog
        active={ineligibleDialogState.active}
        onDismiss={() =>
          setIneligibleDialogState({
            active: false,
            targetFulfillmentType: null,
            targetFulfillmentTypeData: null,
          })
        }
        targetFulfillmentType={ineligibleDialogState.targetFulfillmentType!}
        targetFulfillmentTypeData={
          ineligibleDialogState.targetFulfillmentTypeData
        }
        onConfirm={handleIneligibleDialogConfirm}
      />
    </>
  );
};
